import { useEffect, useMemo, useState } from 'react'
import { Check, Search, Users } from 'lucide-react'

import { Button } from 'components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { Spinner } from 'components/ui/spinner'
import { toast } from 'components/ui/toast-store'
import { cn } from 'lib/utils'

import * as presentationService from '../services/presentationService'
import type { DeckSharing, PresentationVisibility } from '../types/presentation.types'

/**
 * Share a deck with partners (MYD-526): only me, chosen partners, or all of
 * them. Partners it is shared with find it under "Shared with me", can
 * present it and make their own copy, and never edit the original.
 *
 * Each partner sees only the data their own access allows: sharing a deck
 * shares its layout, never more of the data behind it.
 */
interface PartnerSharingDialogProps {
	open: boolean
	presentationId: string
	presentationName?: string
	onClose: () => void
	/** After a save, with what was saved, so a card can update its badge. */
	onSaved?: (sharing: DeckSharing) => void
}

const CHOICES: Array<{ value: PresentationVisibility; label: string; hint: string }> = [
	{ value: 'personal', label: 'Only me', hint: 'Nobody else can open it.' },
	{ value: 'selected', label: 'Selected partners', hint: 'The partners you tick below.' },
	{ value: 'shared', label: 'All my partners', hint: 'Everyone you are partnered with, now and later.' },
]

export function PartnerSharingDialog({ open, presentationId, presentationName, onClose, onSaved }: PartnerSharingDialogProps) {
	const [sharing, setSharing] = useState<DeckSharing | null>(null)
	const [visibility, setVisibility] = useState<PresentationVisibility>('personal')
	const [chosen, setChosen] = useState<Set<string>>(new Set())
	const [query, setQuery] = useState('')
	const [loadError, setLoadError] = useState<string | null>(null)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState<string | null>(null)
	// "Let them know" (MYD-527): on by default, every time the dialog opens.
	const [letThemKnow, setLetThemKnow] = useState(true)

	useEffect(() => {
		if (!open) return
		let cancelled = false
		setSharing(null)
		setLoadError(null)
		setError(null)
		setQuery('')
		setLetThemKnow(true)
		presentationService
			.getSharing(presentationId)
			.then(data => {
				if (cancelled) return
				setSharing(data)
				setVisibility(data.visibility)
				setChosen(new Set(data.sharedWith))
			})
			.catch(() => !cancelled && setLoadError('Sharing could not be loaded.'))
		return () => {
			cancelled = true
		}
	}, [open, presentationId])

	const partners = useMemo(() => {
		const all = sharing?.partners ?? []
		const text = query.trim().toLowerCase()
		return text ? all.filter(partner => `${partner.name} ${partner.email}`.toLowerCase().includes(text)) : all
	}, [sharing, query])

	const toggle = (id: string) =>
		setChosen(current => {
			const next = new Set(current)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})

	const save = async () => {
		setSaving(true)
		setError(null)
		try {
			const saved = await presentationService.updateSharing(presentationId, {
				visibility,
				// The list is sent only when choosing; otherwise the stored one is
				// kept, so switching back later restores it.
				...(visibility === 'selected' ? { sharedWith: [...chosen] } : {}),
				// Only partners newly given access are told; the server works out who.
				notify: visibility !== 'personal' && letThemKnow,
			})
			if (saved.notified) toast.success(`${saved.notified} ${saved.notified === 1 ? 'partner' : 'partners'} notified`)
			onSaved?.(saved)
			onClose()
		} catch (cause: any) {
			setError(cause?.response?.data?.message ?? 'Sharing could not be saved.')
		} finally {
			setSaving(false)
		}
	}

	const noPartners = sharing !== null && sharing.partners.length === 0

	return (
		<Dialog open={open} onClose={() => !saving && onClose()}>
			<DialogContent className="sm:max-w-[520px]">
				<DialogHeader>
					<DialogTitle>Share with partners</DialogTitle>
					<DialogDescription>
						{presentationName ? `Who can open "${presentationName}". ` : ''}Partners can present it and make their own copy, but not edit yours. Each sees only the data
						their own access allows.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 px-5 py-4">
					{loadError ? (
						<p className="text-sm text-destructive">{loadError}</p>
					) : !sharing ? (
						<div className="flex justify-center py-6">
							<Spinner />
						</div>
					) : noPartners ? (
						<div className="flex flex-col items-center py-6 text-center">
							<div className="mb-3 rounded-full bg-gray-100 p-3">
								<Users className="h-5 w-5 text-gray-400" />
							</div>
							<p className="max-w-xs text-sm text-gray-600">You have no partners yet. Invite someone from the Partners page, and you can share decks with them here.</p>
						</div>
					) : (
						<>
							<div className="space-y-1.5" role="radiogroup" aria-label="Who can open it">
								{CHOICES.map(choice => (
									<button
										key={choice.value}
										type="button"
										role="radio"
										aria-checked={visibility === choice.value}
										onClick={() => setVisibility(choice.value)}
										className={cn(
											'flex w-full items-start gap-3 rounded-md border px-3 py-2 text-left transition-colors',
											visibility === choice.value ? 'border-primary bg-primary/5' : 'border-gray-200 hover:bg-gray-50',
										)}
									>
										<span
											className={cn('mt-0.5 h-4 w-4 shrink-0 rounded-full border-2', visibility === choice.value ? 'border-primary bg-primary shadow-[inset_0_0_0_3px_white]' : 'border-gray-300')}
											aria-hidden
										/>
										<span>
											<span className="block text-sm font-medium text-gray-900">{choice.label}</span>
											<span className="block text-xs text-gray-500">{choice.hint}</span>
										</span>
									</button>
								))}
							</div>

							{visibility === 'selected' && (
								<div>
									<div className="relative mb-2">
										<Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
										<Input value={query} onChange={event => setQuery(event.target.value)} placeholder="Find a partner" aria-label="Find a partner" className="pl-8" />
									</div>
									<ul className="max-h-56 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200" aria-label="Partners">
										{partners.map(partner => {
											const checked = chosen.has(partner.id)
											return (
												<li key={partner.id}>
													{/* The whole row is the checkbox, so a click on the name ticks it. */}
													<button
														type="button"
														role="checkbox"
														aria-checked={checked}
														aria-label={`Share with ${partner.name}`}
														onClick={() => toggle(partner.id)}
														className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-gray-50"
													>
														<span
															className={cn('flex h-4 w-4 shrink-0 items-center justify-center rounded border', checked ? 'border-primary bg-primary text-white' : 'border-gray-300 bg-white')}
															aria-hidden
														>
															{checked && <Check className="h-3 w-3" />}
														</span>
														<span className="min-w-0">
															<span className="block truncate text-sm text-gray-900">{partner.name}</span>
															{partner.email && <span className="block truncate text-xs text-gray-500">{partner.email}</span>}
														</span>
													</button>
												</li>
											)
										})}
										{partners.length === 0 && <li className="px-3 py-3 text-sm text-gray-500">No partner matches.</li>}
									</ul>
									<p className="mt-1.5 text-xs text-gray-500" aria-live="polite">
										{chosen.size === 0 ? 'Nobody chosen yet.' : `Shared with ${chosen.size} ${chosen.size === 1 ? 'partner' : 'partners'}.`}
									</p>
								</div>
							)}

							{/* MYD-527. Nothing to tell anyone when only the owner can open it. */}
							{visibility !== 'personal' && (
								<button
									type="button"
									role="checkbox"
									aria-checked={letThemKnow}
									onClick={() => setLetThemKnow(value => !value)}
									className="flex w-full items-start gap-3 rounded-md px-1 py-1 text-left"
								>
									<span
										className={cn('mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border', letThemKnow ? 'border-primary bg-primary text-white' : 'border-gray-300 bg-white')}
										aria-hidden
									>
										{letThemKnow && <Check className="h-3 w-3" />}
									</span>
									<span>
										<span className="block text-sm font-medium text-gray-900">Let them know</span>
										<span className="block text-xs text-gray-500">Emails partners who are newly given access, and adds it to their notifications.</span>
									</span>
								</button>
							)}
						</>
					)}
					{error && <p className="text-sm text-destructive">{error}</p>}
				</div>

				<DialogFooter>
					<Button variant="ghost" isOutline onClick={onClose} disabled={saving}>
						Cancel
					</Button>
					<Button onClick={save} disabled={saving || !sharing || noPartners}>
						{saving ? 'Saving...' : 'Save'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default PartnerSharingDialog
